import React, { useState } from "react";
import styled from "styled-components";
import { useLinkageDashboard } from "../../hooks/useLinkageDashboard";
import { NavBar } from "../../components/common/NavBar";
import { SideMenu } from "../../components/common/SideMenu";
import {
  BackgroundContainer,
  MainColumn as Column,
  ElementContainer,
  NavBarContainer,
  RowContainer,
} from "../../components/containers/styledComponents";
import {
  CompassMainHeader,
  CompassParagraph,
} from "../../components/common/styledComponents";
import { greyText } from "../../styles";
import { CustomDropdown } from "../../components/visuals/PageFilter/PageFilter";
import { PageButton } from "../../components/visuals/PageButton";
import { SearchBar } from "../../components/visuals/SearchBar";
import { ThemeDynamicsChart } from "./ThemeDynamicsChart";
import { MaterialEscalationChart } from "./MaterialEscalationChart";
import { CollaborationTensionChart } from "./CollaborationTensionChart";
import { LinkageTable } from "./LinkageTable";
import { Filter as Option } from "../../types/PageFilterTypes";

// =============================================================================
// CONSTANTS
// =============================================================================

const DEFAULT_MANAGER_FILTER: Option = {
  key: null,
  displayValue: "Select manager",
};

const DEFAULT_COUNTRY_FILTER: Option = {
  key: null,
  displayValue: "Select country",
};

const DEFAULT_GICS_SECTOR_FILTER: Option = {
  key: null,
  displayValue: "Industry",
};

const PAGE_DESCRIPTION =
  "Explore the relationships between engagement activities and voting behaviour across themes, material topics, and managers.";

// =============================================================================
// STYLED COMPONENTS
// =============================================================================

const CardRowContainer = styled.div<{ pl?: string; pr?: string }>`
  display: flex;
  flex: 1;
  padding-left: ${({ pl }): string => pl || "0px"};
  padding-right: ${({ pr }): string => pr || "0px"};
`;

// =============================================================================
// COMPONENT
// =============================================================================

export const LinkageDashboard = (): JSX.Element => {
  const [show, setShow] = useState<boolean>(false);

  const {
    // Filter options
    managerOptions,
    companyOptions,
    countryOptions,
    gicsSectorOptions,
    // Filter setters
    setSelectedManagers,
    setSelectedCompany,
    setSelectedCountries,
    setSelectedGicsSector,
    // Chart data
    themeDynamicsData,
    materialEscalationData,
    collaborationTensionData,
    // Raw data
    collaborationTensionRaw,
    // Loading
    isLoadingOptions,
    isLoadingThemeDynamics,
    isLoadingMaterialEscalation,
    isLoadingCollaborationTension,
    // Heatmap toggle
    heatmapShowPercentage,
    setHeatmapShowPercentage,
    // Enum filters
    enumFilters,
    setEnumFilters,
    // Filter payload
    filterPayload,
  } = useLinkageDashboard();

  const handleCompanySelect = (options: Option[]): void => {
    setSelectedCompany(options);
  };

  return (
    <BackgroundContainer>
      <Column flex={show ? "0 0 15%" : "0 0 0%"} show={show}>
        <SideMenu show={show} setShow={setShow} />
      </Column>
      <Column flex="1" bg="#F5F5F5" overflow={true}>
        <NavBarContainer>
          <RowContainer mt={1} mb={1}>
            <NavBar address={"linkage-dashboard"} />
          </RowContainer>
          <RowContainer ai="flex-start" jc="flex-start" mt={-15}>
            <CompassMainHeader pt={1} ml={12}>
              Linkage Dashboard
            </CompassMainHeader>
          </RowContainer>
          <RowContainer ai="flex-start" jc="flex-start" mt={1}>
            <CompassParagraph ml={12} color={greyText}>
              {PAGE_DESCRIPTION}
            </CompassParagraph>
          </RowContainer>

          {/* Filter Bar */}
          <RowContainer ai="center" jc="flex-start" ml={8} mb={8}>
            <ElementContainer ai="center">
              <CustomDropdown
                options={managerOptions}
                isLoading={isLoadingOptions}
                placeholder={DEFAULT_MANAGER_FILTER}
                isMultiSelect={true}
                onSelectOptions={setSelectedManagers}
              />
              <PageButton
                key={companyOptions[0]?.key || "defaultCompanyPageButtonKey"}
                isLoading={isLoadingOptions}
                content={
                  <SearchBar
                    options={companyOptions}
                    placeholder="Search company"
                    isMultiSelect={true}
                    onSelectOptions={handleCompanySelect}
                  />
                }
              />
              <CustomDropdown
                options={countryOptions}
                isLoading={isLoadingOptions}
                placeholder={DEFAULT_COUNTRY_FILTER}
                isMultiSelect={true}
                onSelectOptions={setSelectedCountries}
              />
              <CustomDropdown
                options={gicsSectorOptions}
                isLoading={isLoadingOptions}
                placeholder={DEFAULT_GICS_SECTOR_FILTER}
                isMultiSelect={true}
                onSelectOptions={setSelectedGicsSector}
              />
            </ElementContainer>
          </RowContainer>
        </NavBarContainer>

        {/* Theme Dynamics – 100% Stacked Bar */}
        <RowContainer>
          <ThemeDynamicsChart
            data={themeDynamicsData}
            isLoading={isLoadingThemeDynamics}
          />
        </RowContainer>

        {/* Linkage Records Table */}
        <RowContainer>
          <LinkageTable
            filters={filterPayload}
            enumFilters={enumFilters}
            setEnumFilters={setEnumFilters}
          />
        </RowContainer>

        {/* Material Escalation Heatmap + Collaboration Scatter side-by-side */}
        <RowContainer mt={-1} ai="flex-start">
          <CardRowContainer pl="8px">
            <MaterialEscalationChart
              data={materialEscalationData}
              isLoading={isLoadingMaterialEscalation}
              showPercentage={heatmapShowPercentage}
              onToggle={setHeatmapShowPercentage}
            />
          </CardRowContainer>
          <CardRowContainer pr="8px">
            <CollaborationTensionChart
              data={collaborationTensionData}
              rawData={collaborationTensionRaw}
              isLoading={isLoadingCollaborationTension}
            />
          </CardRowContainer>
        </RowContainer>
      </Column>
    </BackgroundContainer>
  );
};
